/**
 * Step 3 of the style-template screens: the per-tool options.
 *
 * Each factory returns fresh state for one screen mount. The values are sent
 * as `options` on the job; the server owns the wording (the face is always
 * kept and is never an option).
 */
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import type { JobOptions } from '@loomia/shared';

export interface ToolOptionsUi {
  /** Markup for the options step(s). `step` renders a numbered heading. */
  html(step: (n: number, title: string, extra?: string) => string): string;
  /** Click on an element carrying `data-opt`. Return true to repaint. */
  onClick(el: HTMLElement): boolean;
  /** `change` on an input carrying `data-opt`. Return true to repaint. */
  onChange?(input: HTMLInputElement): boolean;
  value(): JobOptions | undefined;
}

// -- Change Outfit: keep pose --------------------------------------------------
// No "keep background" switch: every outfit template authors its own scene, so
// keeping the user's background would contradict the template.

export function outfitOptions(): ToolOptionsUi {
  let keepPose = true;
  return {
    html: (step) => `
          ${step(3, 'More options')}
          <div class="co-toggles co-toggles-one">
            <button class="co-toggle${keepPose ? ' on' : ''}" role="switch" aria-checked="${keepPose}" data-opt="pose">
              <span class="co-toggle-text"><strong>Keep pose</strong><small>${keepPose ? 'Maintain your pose' : 'Pose fits the outfit'}</small></span>
              <span class="co-knob" aria-hidden="true"></span>
            </button>
          </div>
          <p class="co-sub co-face">Your face is always kept the same. The style sets the background.</p>`,
    onClick(el) {
      if (el.dataset.opt !== 'pose') return false;
      keepPose = !keepPose;
      return true;
    },
    value: () => ({ keepPose }),
  };
}

// -- Hair Salon: hair color --------------------------------------------------

/** UI swatches only; the server decides the wording for each name. */
const HAIR_COLORS: ReadonlyArray<{ id: string; label: string; css: string }> = [
  { id: 'black', label: 'Black', css: '#1c1a1f' },
  { id: 'brown', label: 'Brown', css: '#6b4226' },
  { id: 'blonde', label: 'Blonde', css: '#e3b778' },
  { id: 'red', label: 'Red', css: '#b83a47' },
  { id: 'purple', label: 'Purple', css: '#8a4fd0' },
];
const HEX = /^#[0-9a-f]{6}$/i;

export function hairOptions(): ToolOptionsUi {
  /** '' = keep the style's own color (as in its example); a name from HAIR_COLORS; or a '#rrggbb' hex. */
  let color = '';
  let custom = '#8a4fd0';
  const isCustom = (): boolean => HEX.test(color);

  const swatch = (id: string, label: string, inner: string, selected: boolean): string => `
        <button class="co-color${selected ? ' selected' : ''}" role="radio" aria-checked="${selected}" data-opt="color" data-color="${escapeHtml(id)}">
          ${inner}<span>${escapeHtml(label)}</span>
        </button>`;

  return {
    html: (step) => `
          ${step(3, 'Hair color <span class="co-sub">(Optional)</span>')}
          <div class="co-colors" role="radiogroup" aria-label="Hair color">
            ${swatch('', 'Style', `<i class="co-dot co-dot-original">${icon('format_color_reset')}</i>`, color === '')}
            ${HAIR_COLORS.map((c) => swatch(c.id, c.label, `<i class="co-dot" style="background:${c.css}"></i>`, color === c.id)).join('')}
            <label class="co-color${isCustom() ? ' selected' : ''}" aria-label="Pick a custom color">
              <i class="co-dot co-dot-more"${isCustom() ? ` style="background:${escapeHtml(color)}"` : ''}></i>
              <input type="color" class="co-picker" data-opt="hex" value="${escapeHtml(isCustom() ? color : custom)}" />
              <span>${isCustom() ? escapeHtml(color.toUpperCase()) : 'More'}</span>
            </label>
          </div>
          <p class="co-sub co-face">"Style" keeps the color shown in the example. Your face is always kept the same; the style sets the background.</p>`,
    onClick(el) {
      if (el.dataset.opt !== 'color') return false;
      color = el.dataset.color ?? '';
      return true;
    },
    onChange(input) {
      if (input.dataset.opt !== 'hex' || !HEX.test(input.value)) return false;
      custom = input.value.toLowerCase();
      color = custom;
      return true;
    },
    value: () => (color ? { hairColor: color } : undefined),
  };
}

// -- Makeup Styles: intensity + natural skin texture ---------------------------

const INTENSITIES: ReadonlyArray<{ id: 'light' | 'medium' | 'bold'; label: string }> = [
  { id: 'light', label: 'Light' },
  { id: 'medium', label: 'Medium' },
  { id: 'bold', label: 'Bold' },
];

export function makeupOptions(): ToolOptionsUi {
  let intensity: 'light' | 'medium' | 'bold' = 'medium';
  let keepSkinTexture = true;
  return {
    html: (step) => `
          ${step(3, 'Intensity')}
          <div class="co-seg" role="radiogroup" aria-label="Makeup intensity">
            ${INTENSITIES.map(
              (i) => `<button class="co-seg-opt${i.id === intensity ? ' selected' : ''}" role="radio" aria-checked="${i.id === intensity}" data-opt="intensity" data-intensity="${i.id}">${i.label}</button>`,
            ).join('')}
          </div>
          <div class="co-toggles co-toggles-one">
            <button class="co-toggle${keepSkinTexture ? ' on' : ''}" role="switch" aria-checked="${keepSkinTexture}" data-opt="skin">
              <span class="co-toggle-text"><strong>Natural skin texture</strong><small>${keepSkinTexture ? 'Keeps pores and fine detail' : 'Softer, more polished skin'}</small></span>
              <span class="co-knob" aria-hidden="true"></span>
            </button>
          </div>
          <p class="co-sub co-face">Your face and skin tone are always kept the same</p>`,
    onClick(el) {
      if (el.dataset.opt === 'intensity') {
        const v = el.dataset.intensity;
        if (v !== 'light' && v !== 'medium' && v !== 'bold') return false;
        intensity = v;
      } else if (el.dataset.opt === 'skin') keepSkinTexture = !keepSkinTexture;
      else return false;
      return true;
    },
    value: () => ({ intensity, keepSkinTexture }),
  };
}

// -- Face Expression: intensity only -------------------------------------------

const EXPRESSION_INTENSITIES: ReadonlyArray<{ id: 'subtle' | 'natural' | 'strong'; label: string }> = [
  { id: 'subtle', label: 'Subtle' },
  { id: 'natural', label: 'Natural' },
  { id: 'strong', label: 'Strong' },
];

export function expressionOptions(): ToolOptionsUi {
  let intensity: 'subtle' | 'natural' | 'strong' = 'natural';
  return {
    html: (step) => `
          ${step(3, 'Expression intensity')}
          <div class="co-seg" role="radiogroup" aria-label="Expression intensity">
            ${EXPRESSION_INTENSITIES.map(
              (i) => `<button class="co-seg-opt${i.id === intensity ? ' selected' : ''}" role="radio" aria-checked="${i.id === intensity}" data-opt="intensity" data-intensity="${i.id}">${i.label}</button>`,
            ).join('')}
          </div>
          <p class="co-sub co-face">Your identity and skin tone stay the same</p>`,
    onClick(el) {
      if (el.dataset.opt !== 'intensity') return false;
      const v = el.dataset.intensity;
      if (v !== 'subtle' && v !== 'natural' && v !== 'strong') return false;
      intensity = v;
      return true;
    },
    value: () => ({ intensity }),
  };
}
